<template>
    <Dialog :open="open" @update:open="$emit('close')">
        <DialogContent class="sm:max-w-md">
            <DialogHeader>
                <DialogTitle>We're sorry to see you go</DialogTitle>
            </DialogHeader>

            <div class="space-y-4">
                <p class="text-sm text-muted-foreground">
                    Please help us improve by sharing why you're canceling.
                </p>

                <div v-if="loadingReasons" class="space-y-2">
                    <div v-for="i in 4" :key="i" class="h-12 rounded-md bg-new-neutral-100 animate-pulse"></div>
                </div>

                <div v-else-if="reasons.length" class="space-y-2">
                    <label
                        v-for="reason in reasons"
                        :key="String(reason.value)"
                        class="flex cursor-pointer items-center gap-3 rounded-md border border-new-neutral-200 p-3 hover:bg-new-neutral-50"
                        :class="{ 'border-primary bg-new-neutral-50': selectedReasons.includes(String(reason.value)) }"
                    >
                        <input
                            v-model="selectedReasons"
                            type="checkbox"
                            :value="String(reason.value)"
                            class="size-4 rounded border-new-neutral-300"
                        />
                        <span class="text-sm text-foreground">{{ reason.title }}</span>
                    </label>
                </div>

                <div>
                    <Label class="text-sm text-muted-foreground">Other reason (optional)</Label>
                    <Input v-model="otherReason" class="mt-2" placeholder="Tell us more..." />
                </div>
            </div>

            <DialogFooter class="mt-4">
                <Button size="lg" variant="ghost" @click="$emit('close')">Skip</Button>
                <Button size="lg" :disabled="loading || (selectedReasons.length === 0 && !otherReason)" @click="submit">
                    Submit feedback
                </Button>
            </DialogFooter>
        </DialogContent>
    </Dialog>
</template>

<script lang="ts" setup>
import { ref, watch } from 'vue';
import axios from 'axios';
import { router } from '@inertiajs/vue3';
import { route } from 'ziggy-js';
import { toast } from 'vue-sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import ISelectable from '@/types/Atomic/ISelectable';

const props = defineProps<{
    open: boolean;
    subscriptionId: number;
}>();

const emit = defineEmits<{
    close: [];
}>();

const reasons = ref<ISelectable[]>([]);
const selectedReasons = ref<string[]>([]);
const otherReason = ref('');
const loading = ref(false);
const loadingReasons = ref(false);

const fetchReasons = async () => {
    loadingReasons.value = true;
    try {
        const res = await axios.get(route('subscriptions.cancel-reasons.index'));
        reasons.value = res.data.data || [];
    } catch {
        // Silently fail
    } finally {
        loadingReasons.value = false;
    }
};

watch(() => props.open, (val) => {
    if (val) {
        selectedReasons.value = [];
        otherReason.value = '';
        if (reasons.value.length === 0) {
            fetchReasons();
        }
    }
});

const submit = () => {
    loading.value = true;

    const data = [...selectedReasons.value];
    if (otherReason.value.trim()) {
        const otherIndex = data.indexOf('Other');
        if (otherIndex !== -1) {
            data[otherIndex] = `Other: ${otherReason.value.trim()}`;
        } else {
            data.push(`Other: ${otherReason.value.trim()}`);
        }
    }

    router.put(
        route('subscriptions.update', { subscription: props.subscriptionId }),
        { data },
        {
            preserveState: true,
            preserveScroll: true,
            onSuccess: () => {
                toast.success('Thank you for your feedback');
                emit('close');
            },
            onError: (errors) => {
                const message = (Object.values(errors)[0] as string) || 'Failed to submit feedback';
                toast.error(message);
            },
            onFinish: () => {
                loading.value = false;
            },
        }
    );
};
</script>
